import { haversineMeters } from '../geo/geo';
import type { TypeHint } from './names';

/**
 * Location scoring and decision policy (docs/location-intelligence.md §6). Pure: the resolver
 * gathers candidates (gazetteer, tenant places, provider) and describes each one with features;
 * this module turns them into a confidence and decides ACCEPT / CONFIRM / CHOOSE / CLARIFY.
 *
 * Two independent axes:
 *  - identity: is this the place the user meant? (score 0..1)
 *  - precision: is the point good enough to send a vehicle? (radiusM vs the role's limit)
 */

/** Bumped whenever weights or thresholds change; stored with every logged resolution. */
export const SCORING_VERSION = 3;

export const WEIGHTS = { name: 0.45, admin: 0.3, type: 0.15, quality: 0.1 } as const;
export const THRESHOLDS = {
  /** identity accepted without asking */
  accept: 0.85,
  /** minimum lead over the best *different* place to accept or confirm a single candidate */
  margin: 0.1,
  /** a single candidate is worth a yes/no question */
  confirm: 0.65,
  /** a candidate is worth showing in a list */
  list: 0.55,
} as const;

export interface Thresholds {
  accept: number;
  margin: number;
  confirm: number;
  list: number;
}

/**
 * What a tenant may change. The lower bound of each threshold is the platform value: a tenant can
 * ask more questions (stricter), never accept with less evidence than the platform allows.
 */
export const THRESHOLD_BOUNDS: Record<keyof Thresholds, readonly [min: number, max: number]> = {
  accept: [THRESHOLDS.accept, 0.97],
  margin: [THRESHOLDS.margin, 0.25],
  confirm: [THRESHOLDS.confirm, 0.84],
  list: [THRESHOLDS.list, 0.7],
};

/** Tenant overrides clamped to the platform bounds; anything unknown or out of order is ignored. */
export function resolveThresholds(overrides?: Partial<Record<keyof Thresholds, unknown>> | null): Thresholds {
  const out: Thresholds = { ...THRESHOLDS };
  for (const k of Object.keys(THRESHOLD_BOUNDS) as Array<keyof Thresholds>) {
    const v = overrides?.[k];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const [min, max] = THRESHOLD_BOUNDS[k];
    out[k] = Math.min(max, Math.max(min, Math.round(v * 1000) / 1000));
  }
  // the ladder must stay ordered: list <= confirm < accept
  if (out.confirm >= out.accept) out.confirm = Math.min(out.confirm, out.accept - 0.01);
  if (out.list > out.confirm) out.list = out.confirm;
  return out;
}

/** Largest uncertainty (metres) that can be dispatched / priced without asking for more detail. */
export const DISPATCHABLE_RADIUS_M = { PICKUP: 75, DROPOFF: 1000 } as const;
export type LocationRole = keyof typeof DISPATCHABLE_RADIUS_M;

export type PlaceKind =
  | 'department'
  | 'subregion'
  | 'municipality'
  | 'corregimiento'
  | 'centro_poblado'
  | 'vereda'
  | 'barrio'
  | 'sector'
  | 'urbanizacion'
  | 'conjunto'
  | 'poi'
  | 'landmark'
  | 'pickup_point'
  | 'road'
  | 'address';

export const PLACE_KINDS: readonly PlaceKind[] = [
  'department', 'subregion', 'municipality', 'corregimiento', 'centro_poblado', 'vereda', 'barrio', 'sector',
  'urbanizacion', 'conjunto', 'poi', 'landmark', 'pickup_point', 'road', 'address',
];

/** How the candidate's name relates to what the user wrote. */
export type NameMatch =
  | { type: 'official' }
  | { type: 'alias'; weight: number }
  | { type: 'key' }
  | { type: 'phonetic' }
  | { type: 'fuzzy'; similarity: number }
  /** the candidate's name contains the query ("Colegio El Porvenir" for "El Porvenir"): points of interest only */
  | { type: 'contains' }
  /**
   * every word the user wrote is in the name, not as one phrase ("iglesia la asuncion" for "Iglesia de
   * Nuestra Señora de la Asunción"); `coverage` is the share of the name written. Never accepted alone
   */
  | { type: 'words'; coverage: number }
  /** a street address matched structurally (corner / plate); the name is not the question */
  | { type: 'structural'; exact: boolean }
  /**
   * the provider matched the name to a place it returns without its name (Google Geocoding answers an
   * establishment with its street address): plausible, never certain, so it is confirmed
   */
  | { type: 'provider' };

/** Administrative relation between the candidate and what we know about where the user is. */
export type AdminRelation =
  /** the user wrote this municipality (or a department containing it) */
  | { type: 'explicit'; inHomeDepartment: boolean }
  /** no municipality written: the candidate is in the context municipality, with the context's strength */
  | { type: 'context'; strength: number }
  /** another municipality the tenant operates in */
  | { type: 'operating' }
  /** a municipality near the context (not operated) */
  | { type: 'neighbor' }
  | { type: 'department' }
  | { type: 'other' }
  /** administrative data unknown (typed coordinates, offline provider) */
  | { type: 'unknown' };

export type SourceQuality = 'verified' | 'tenant' | 'official' | 'osm_polygon' | 'osm_node' | 'osm_poi' | 'provider' | 'pending';

/**
 * What our own operation knows about a candidate (never the provider's opinion). All optional: a
 * candidate without evidence scores exactly as before.
 */
export interface CandidateEvidence {
  /** times the point was confirmed by evidence we own: valid driver arrivals, operator pins, approved reviews */
  confirmations?: number;
  /** days since the last confirmation */
  confirmedDaysAgo?: number | null;
  /** resolutions of this place that ended well / were corrected afterwards (recent window) */
  resolved?: number;
  corrected?: number;
  /** learned and not yet reviewed by a person */
  unreviewed?: boolean;
}

export interface CandidateFeatures {
  name: NameMatch;
  admin: AdminRelation;
  kind: PlaceKind;
  typeHint: TypeHint | null;
  quality: SourceQuality;
  evidence?: CandidateEvidence | null;
}

const QUALITY: Record<SourceQuality, number> = {
  verified: 1,
  tenant: 0.95,
  official: 0.95,
  osm_polygon: 0.85,
  osm_node: 0.75,
  osm_poi: 0.7,
  provider: 0.6,
  pending: 0.5,
};

export function nameScore(m: NameMatch): number {
  switch (m.type) {
    case 'official':
      return 1;
    case 'alias':
      return Math.min(1, Math.max(0, m.weight));
    case 'key':
      return 0.92;
    case 'phonetic':
      return 0.85;
    case 'fuzzy':
      return m.similarity < 0.55 ? 0 : Math.min(0.8, 0.6 + 0.44 * (m.similarity - 0.55));
    case 'contains':
      return 0.4;
    case 'words':
      return Math.min(0.6, 0.45 + 0.3 * Math.max(0, m.coverage));
    case 'structural':
      return m.exact ? 1 : 0.6;
    case 'provider':
      return 0.6;
  }
}

export function adminScore(a: AdminRelation): number {
  switch (a.type) {
    case 'explicit':
      return a.inHomeDepartment ? 1 : 0.8;
    case 'context':
      return Math.min(0.9, Math.max(0, a.strength));
    case 'operating':
      return 0.45;
    case 'neighbor':
      return 0.3;
    case 'department':
      return 0.15;
    case 'other':
    case 'unknown':
      return 0;
  }
}

const URBAN: ReadonlySet<PlaceKind> = new Set(['barrio', 'urbanizacion', 'conjunto', 'sector']);
const RURAL: ReadonlySet<PlaceKind> = new Set(['vereda', 'corregimiento', 'centro_poblado', 'sector']);

/** 1 exact kind, 0.7 unknown or compatible, 0.3 contradicts the hint (asked for a vereda, got a barrio). */
export function typeScore(kind: PlaceKind, hint: TypeHint | null): number {
  if (!hint) return 0.7;
  // "cerca del aeropuerto": a site is meant; an area of the same name stays possible ("cerca de San Antonio")
  if (hint === 'landmark') return kind === 'poi' || kind === 'landmark' || kind === 'pickup_point' ? 1 : 0.7;
  if (kind === hint) return 1;
  const hintUrban = URBAN.has(hint);
  const hintRural = RURAL.has(hint);
  if ((hintUrban && URBAN.has(kind)) || (hintRural && RURAL.has(kind))) return 0.7;
  if (URBAN.has(kind) || RURAL.has(kind)) return 0.3;
  // a point of interest, a road or a municipality when an area type was asked for
  return 0.5;
}

/** Bounds of the evidence adjustment: it tips close calls, it never replaces name and context. */
export const EVIDENCE = {
  /** most a well confirmed place can gain */
  maxBonus: 0.05,
  /** confirmations at which the bonus saturates */
  saturation: 20,
  /** a confirmation loses half its value after this many days */
  halfLifeDays: 180,
  /** most a place with a bad record can lose */
  maxPenalty: 0.1,
  /** resolutions needed before the correction rate says anything */
  minHistory: 5,
  /** knowledge nobody reviewed yet */
  unreviewedPenalty: 0.05,
} as const;

/**
 * Evidence adjustment in [-maxPenalty - unreviewedPenalty, +maxBonus].
 *  - confirmations (GPS arrivals, human pins) add up to +0.05, log-scaled and decayed by age;
 *  - a high share of corrected resolutions subtracts up to 0.10;
 *  - how often a name is used adds NOTHING: popularity is not correctness.
 */
export function evidenceAdjustment(e: CandidateEvidence | null | undefined): number {
  if (!e) return 0;
  let adj = 0;
  const n = Math.max(0, e.confirmations ?? 0);
  if (n > 0) {
    const volume = Math.min(1, Math.log1p(n) / Math.log1p(EVIDENCE.saturation));
    const age = e.confirmedDaysAgo === null || e.confirmedDaysAgo === undefined ? 1 : Math.pow(0.5, Math.max(0, e.confirmedDaysAgo) / EVIDENCE.halfLifeDays);
    adj += EVIDENCE.maxBonus * volume * age;
  }
  const history = (e.resolved ?? 0) + (e.corrected ?? 0);
  if (history >= EVIDENCE.minHistory) adj -= EVIDENCE.maxPenalty * ((e.corrected ?? 0) / history);
  if (e.unreviewed) adj -= EVIDENCE.unreviewedPenalty;
  return Math.round(adj * 1000) / 1000;
}

/** The parts of a score, for the log and the admin ("why was this accepted?"). */
export interface ScoreExplanation {
  name: number;
  admin: number;
  type: number;
  quality: number;
  evidence: number;
  score: number;
}

export function explainScore(f: CandidateFeatures): ScoreExplanation {
  const r = (v: number) => Math.round(v * 1000) / 1000;
  const name = nameScore(f.name);
  const admin = adminScore(f.admin);
  const type = typeScore(f.kind, f.typeHint);
  const quality = QUALITY[f.quality];
  const evidence = evidenceAdjustment(f.evidence);
  const base = WEIGHTS.name * name + WEIGHTS.admin * admin + WEIGHTS.type * type + WEIGHTS.quality * quality;
  return { name: r(name), admin: r(admin), type: r(type), quality: r(quality), evidence, score: r(Math.min(1, Math.max(0, base + evidence))) };
}

export function scoreCandidate(f: CandidateFeatures): number {
  return explainScore(f).score;
}

export interface ScoredCandidate {
  /** stable identity used to merge duplicates and to answer choices */
  key: string;
  kind?: PlaceKind;
  lat: number;
  lng: number;
  radiusM: number;
  score: number;
}

export type Decision = 'ACCEPT' | 'CONFIRM' | 'CHOOSE' | 'CLARIFY';

export interface DecisionResult<C extends ScoredCandidate> {
  decision: Decision;
  best: C | null;
  /** distinct places worth showing (best first), at most `maxOptions` */
  options: C[];
  /** best score minus the best score of a different place (1 when there is no other) */
  margin: number;
}

/** Administrative areas: a barrio and a vereda with the same name are two places even when they touch. */
const ADMIN_AREAS: ReadonlySet<PlaceKind> = new Set(['barrio', 'vereda', 'corregimiento']);

/** Two candidates are the same place when they are closer than the larger of their radii (and 200 m). */
export function samePlace(a: ScoredCandidate, b: ScoredCandidate): boolean {
  if (a.key === b.key) return true;
  if (a.kind && b.kind && a.kind !== b.kind && ADMIN_AREAS.has(a.kind) && ADMIN_AREAS.has(b.kind)) return false;
  return haversineMeters(a, b) <= Math.max(a.radiusM, b.radiusM, 200);
}

/**
 * The decision. Never guesses: a strong, clearly leading identity is accepted; one plausible
 * candidate is confirmed; several are listed; nothing good enough means asking for more data.
 */
export function decide<C extends ScoredCandidate>(candidates: readonly C[], maxOptions = 3, t: Thresholds = THRESHOLDS): DecisionResult<C> {
  const sorted = [...candidates].sort((a, b) => b.score - a.score);
  const best = sorted[0] ?? null;
  if (!best || best.score < t.list) return { decision: 'CLARIFY', best: null, options: [], margin: 0 };
  const distinct: C[] = [];
  for (const c of sorted) if (!distinct.some((d) => samePlace(d, c))) distinct.push(c);
  const second = distinct[1];
  const margin = Math.round((best.score - (second?.score ?? 0)) * 1000) / 1000;
  const options = distinct.filter((c) => c.score >= t.list).slice(0, maxOptions);
  if (best.score >= t.accept && margin >= t.margin) return { decision: 'ACCEPT', best, options, margin };
  if (best.score >= t.confirm) {
    if (options.length === 1 || margin >= t.margin) return { decision: 'CONFIRM', best, options: [best], margin };
    return { decision: 'CHOOSE', best, options, margin };
  }
  // weak everywhere: ask for more data, offering what we have as suggestions
  return { decision: 'CLARIFY', best: null, options, margin };
}

/** Is a point with this uncertainty precise enough for the role (pickup: a vehicle goes there)? */
/**
 * A destination that is one site (an airport, a campus, a hospital) prices right at its center even
 * when its grounds are wide: the whole site is the destination. A pickup still needs the exact door.
 */
export const DROPOFF_SITE_RADIUS_M = 3000;
const SITES: ReadonlySet<PlaceKind> = new Set(['poi', 'landmark', 'pickup_point']);

export function isDispatchable(role: LocationRole, radiusM: number, kind?: PlaceKind): boolean {
  if (role === 'DROPOFF' && kind && SITES.has(kind)) return radiusM <= DROPOFF_SITE_RADIUS_M;
  return radiusM <= DISPATCHABLE_RADIUS_M[role];
}

/** Default uncertainty radius of a place kind when its geometry is unknown (metres). */
export const DEFAULT_RADIUS_M: Record<PlaceKind, number> = {
  department: 100_000,
  subregion: 40_000,
  municipality: 5_000,
  corregimiento: 1_500,
  centro_poblado: 800,
  vereda: 2_000,
  barrio: 400,
  sector: 600,
  urbanizacion: 250,
  conjunto: 120,
  poi: 60,
  landmark: 150,
  pickup_point: 25,
  road: 1_000,
  address: 30,
};
